import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import React, { useCallback, useRef } from 'react';

import GridItemCount from '@sophocles/data-grids/components/cells/GridItemCount';

const buildInputSx = radius => ({
  borderRadius: radius,
  // Pin to the same fixed height as the toolbar's ToggleButton segments and
  // ToolbarIconButton (both sizeSmall.height: var(--filter-chip-height)) — the
  // filled-input's own padding/border otherwise settles a couple pixels taller,
  // so the search field visibly disagrees with its toolbar neighbors.
  height: 'var(--filter-chip-height)',
  backgroundColor: 'var(--input-bg)',
  border: '1px solid var(--input-border)',
  '&:hover': {
    backgroundColor: 'var(--input-bg)',
    borderColor: 'var(--input-border-hover)',
  },
  '&.Mui-focused': {
    backgroundColor: 'var(--input-bg)',
    borderColor: 'var(--input-focus-border)',
    boxShadow: 'var(--input-focus-shadow), var(--input-shadow)',
  },
  '&.Mui-disabled': {
    backgroundColor: 'var(--input-disabled-bg)',
    borderColor: 'var(--input-disabled-color)',
  },
});

/**
 * Filled-variant, hidden-label search input that matches the
 * DataGridToolbar's quick-filter input. Controlled — accepts `value`,
 * `onChange(value)`, and an optional `placeholder`.
 *
 * Use this for any in-app search field that should visually pair with
 * the toolbar's quick-filter chrome (e.g. side panels, secondary
 * filter bars).
 */
export default function ToolbarSearchInput({
  value,
  onChange,
  placeholder,
  sx,
  autoFocus,
  inputRef: externalInputRef,
  inputProps,
  itemCount,
  countNoun,
  // Field corner radius. Defaults to the toolbar button radius; mobile
  // callers pass a softer pill radius for a more touch-friendly search bar.
  radius = 'var(--button-radius)',
  ...rest
}) {
  const localInputRef = useRef(null);
  const inputRef = externalInputRef || localInputRef;

  const handleClear = useCallback(() => {
    onChange('');
    inputRef.current?.focus();
  }, [onChange, inputRef]);

  // The matched-row count sits at the trailing edge of the field, before
  // the clear button. GridItemCount renders nothing until a count exists.
  const endAdornment =
    itemCount != null || value ? (
      <InputAdornment position="end">
        <GridItemCount count={itemCount} noun={countNoun} sx={{ mr: value ? 0.5 : 0 }} />
        {value ? (
          <IconButton
            size="small"
            edge="end"
            onClick={handleClear}
            onMouseDown={e => e.preventDefault()}
            aria-label="Clear search"
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        ) : null}
      </InputAdornment>
    ) : null;

  return (
    <TextField
      inputRef={inputRef}
      size="small"
      variant="filled"
      hiddenLabel
      placeholder={placeholder || 'Search...'}
      value={value}
      onChange={e => onChange(e.target.value)}
      autoFocus={autoFocus}
      sx={{ flex: 1, minWidth: 0, ...sx }}
      slotProps={{
        htmlInput: inputProps,
        input: {
          disableUnderline: true,
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment,
          sx: buildInputSx(radius),
        },
      }}
      {...rest}
    />
  );
}
